import { ProposalRow } from '@components/BookingProposal/ProposalRow';
import { ProposalRowsSkeleton } from '@components/BookingProposal/ProposalRowsSkeleton';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NoddiBasicCard, NoddiLink, NoddiSkeleton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { usePendingBookingProposals } from '../../../hooks/usePendingBookingProposals';

type Props = {
  userGroupId: number;
};

/** Rows shown on the home page; the rest live on the full list. */
const HOME_ROW_LIMIT = 3;

export const PendingBookingProposals = ({ userGroupId }: Props) => {
  const { proposals, totalCount, error, isPending } = usePendingBookingProposals(userGroupId, {
    pageSize: HOME_ROW_LIMIT
  });

  if (isPending) {
    return (
      <div className='flex flex-col gap-2 md:gap-4'>
        <NoddiSkeleton variant='button' className='h-7 w-72 max-w-full md:h-9' />
        <NoddiSkeleton variant='button' className='h-4 w-full max-w-xl' />
        <ProposalRowsSkeleton />
      </div>
    );
  }

  if (error) {
    return <ApiErrorMessageWithTrans error={error} />;
  }

  if (proposals.length === 0) {
    return null;
  }

  const count = totalCount;
  const remainingCount = count - proposals.length;
  const listPath = customerRoutes.bookingProposals.getPath();

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      {/* No "see all" link beside the heading: it pointed at the same list as the row
          below it, so the section offered the same jump twice. */}
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        {plural(count, { one: '# booking needs confirmation', other: '# bookings need confirmation' })}
      </h2>
      <p className='text-muted-foreground'>
        <Trans>
          These vehicles are due for service. We have set up the time and the price — confirm, and we take care of the
          rest.
        </Trans>
      </p>
      <NoddiBasicCard className='flex flex-col divide-y divide-border overflow-hidden p-0'>
        {proposals.map((proposal) => (
          <ProposalRow key={proposal.id} proposal={proposal} />
        ))}
        {remainingCount > 0 && (
          <NoddiLink
            to={listPath}
            variant='primary'
            endIcon='ChevronRight'
            className='h-auto w-full justify-center rounded-none py-4'
          >
            {/* "Confirm" belongs to the button that books; this one only opens the list. */}
            {plural(remainingCount, { one: 'See the last one', other: 'See the other #' })}
          </NoddiLink>
        )}
      </NoddiBasicCard>
    </div>
  );
};
